import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * HTTP Interceptor that adds X-Correlation-Id header to all outgoing requests.
 * Generates a new correlation ID for each request to enable distributed tracing.
 * Preserves existing correlation ID if already present in the request.
 */
@Injectable()
export class CorrelationIdInterceptor implements HttpInterceptor {
    private readonly CORRELATION_ID_HEADER = 'X-Correlation-Id';
    private readonly CORRELATION_ID_STORAGE_KEY = 'correlationId';
    
    /**
     * Intercepts HTTP requests and adds X-Correlation-Id header.
     * @param req The outgoing request
     * @param next The next handler in the chain
     * @returns Observable of the HTTP event
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Check if the request already has a correlation ID header
        if (req.headers.has(this.CORRELATION_ID_HEADER)) {
            // Already has correlation ID, pass through
            return next.handle(req);
        }
        
        // Try to get correlation ID from various sources in order of priority:
        // 1. Session storage (persisted across page navigations)
        // 2. Window object (set by server-rendered page)
        // 3. Generate new GUID
        let correlationId = this.getOrCreateCorrelationId();
        
        // Clone the request and add the header
        const clonedRequest = req.clone({
            headers: req.headers.set(this.CORRELATION_ID_HEADER, correlationId)
        });
        
        return next.handle(clonedRequest);
    }
    
    /**
     * Gets existing correlation ID or creates a new one.
     * Checks multiple sources in priority order.
     * @returns The correlation ID string
     */
    private getOrCreateCorrelationId(): string {
        // Priority 1: Check if correlation ID exists in session storage
        try {
            const storedId = sessionStorage.getItem(this.CORRELATION_ID_STORAGE_KEY);
            if (storedId) {
                return storedId;
            }
        } catch (e) {
            // Session storage might not be available, continue
        }
        
        // Priority 2: Check if correlation ID was set by the server in window object
        if (typeof window !== 'undefined' && (window as any).correlationId) {
            const windowId = (window as any).correlationId;
            this.storeCorrelationId(windowId);
            return windowId;
        }
        
        // Priority 3: Generate new GUID
        const newId = this.generateGuid();
        this.storeCorrelationId(newId);
        return newId;
    }
    
    /**
     * Stores correlation ID in session storage for reuse.
     * @param correlationId The correlation ID to store
     */
    private storeCorrelationId(correlationId: string): void {
        try {
            sessionStorage.setItem(this.CORRELATION_ID_STORAGE_KEY, correlationId);
        } catch (e) {
            // Session storage might not be available, ignore
        }
    }
    
    /**
     * Generates a GUID (Globally Unique Identifier) for correlation ID.
     * @returns A GUID string in the format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
     */
    private generateGuid(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
}
